// apps/web/src/pages/estoque/historico-lote-sheet.tsx — Fase 13, Plano 14 (EST-09, D-22).
// Sheet SOMENTE LEITURA com o histórico de um lote de cliente: movimentos de quantidade e mudanças de localização.
// Mudança feita pelo processo aparece como "Automática — {evento}" (chip Workflow); a transferência manual como "Manual".
// O histórico é imutável: sem editar nem excluir; correção é feita por estorno.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Workflow } from 'lucide-react';
import type { HistoricoLoteLinha } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import { rotuloLinhaHistoricoLote } from '../../lib/estoque/saldos-clientes';
import { formatarDataHora } from '../../lib/vendas/formatacao';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

export interface LoteDoHistorico {
  readonly id: string;
  readonly loteInterno: string;
  readonly clienteNome: string;
  readonly unidade: string;
}

interface HistoricoLoteSheetProps {
  /** Lote cujo histórico é mostrado; `null` mantém o Sheet fechado. */
  lote: LoteDoHistorico | null;
  onFechar: () => void;
}

function BadgeTipo({ linha }: { linha: HistoricoLoteLinha }) {
  const rotulo = rotuloLinhaHistoricoLote(linha);
  if (linha.origem === 'LOCALIZACAO') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
        <Workflow className="size-3" aria-hidden="true" />
        {rotulo}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">
      {rotulo}
    </span>
  );
}

function textoLocal(linha: HistoricoLoteLinha): string {
  if (linha.origem === 'LOCALIZACAO') return `${linha.deNome ?? '—'} → ${linha.paraNome ?? '—'}`;
  return linha.paraNome ?? '—';
}

function ConteudoHistorico({ lote }: { lote: LoteDoHistorico }) {
  const [itens, setItens] = useState<readonly HistoricoLoteLinha[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/estoque/saldos/lotes/${encodeURIComponent(lote.id)}/historico`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as HistoricoLoteLinha[];
        if (!sinal || sinal.vivo) setItens(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [lote.id],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  const linhas = itens ?? [];

  return (
    <>
      <SheetHeader className="pb-0">
        <SheetTitle className="pr-8 text-xl leading-tight">
          Histórico — lote {lote.loteInterno}
        </SheetTitle>
        <SheetDescription>
          {lote.clienteNome}. Do mais recente para o mais antigo. Mudanças de local feitas pelo processo aparecem como &quot;Automática — evento&quot;;
          transferências manuais, como &quot;Manual&quot;.
        </SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {erro ? (
          <div role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o histórico. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </div>
        ) : carregando && itens === null ? (
          <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando histórico do lote">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : linhas.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Nenhum movimento neste período.</p>
        ) : (
          <table className="w-full text-sm" aria-busy={carregando}>
            <caption className="sr-only">Histórico do lote {lote.loteInterno}</caption>
            <thead className="sticky top-0 z-10 bg-slate-100 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Data e hora</th>
                <th scope="col" className={CABECALHO}>Tipo</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => {
                const motivo = linha.justificativa.trim();
                return (
                  <tr key={`${linha.origem}-${linha.id}`} className="border-t align-top">
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatarDataHora(linha.criadoEm)}</td>
                    <td className="px-3 py-2">
                      <BadgeTipo linha={linha} />
                      <p className="mt-1 text-xs text-muted-foreground">
                        {textoLocal(linha)}
                        {linha.usuarioNome ? ` · ${linha.usuarioNome}` : ''}
                      </p>
                      {motivo !== '' && <p className="mt-1 break-words text-xs text-muted-foreground">{motivo}</p>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {linha.quantidade === null ? '—' : formatarQuantidadeEstoque(Number(linha.quantidade), lote.unidade)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="sticky bottom-0 mt-auto border-t bg-background px-4 py-3 text-sm text-muted-foreground">
        O histórico não é editado. Correções são feitas por estorno.
      </p>
    </>
  );
}

export function HistoricoLoteSheet({ lote, onFechar }: HistoricoLoteSheetProps) {
  return (
    <Sheet open={lote !== null} onOpenChange={(aberto) => !aberto && onFechar()}>
      <SheetContent className="w-full gap-3 sm:max-w-2xl">
        {/* key: ao trocar de lote a consulta recomeça do zero */}
        {lote && <ConteudoHistorico key={lote.id} lote={lote} />}
      </SheetContent>
    </Sheet>
  );
}
